import {
  FacebookRounded,
  Instagram,
  LinkedIn,
  Twitter,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Grid,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import PageSection from "components/shared/PageSection";
import { usePageContext } from "contexts/PageContextProvider";
import LazyLoad from "react-lazy-load";
// assets
import NIHLogo from "assets/images/logos/nci_logo_4.png";

/**
 * * Contact section of the Homepage
 * @returns {React.ReactElement} Contact
 */
export default function Contact() {
  return (
    <PageSection
      id="contact-section"
      sx={{ py: 4 }}
      backgroundSx={{ backgroundColor: (theme) => theme.palette.primary.main }}
    >
      <Typography variant="h2" color="common.white" pb={1}>
        Contact us!
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Grid container height="100%">
            <Grid item xs={6} md={12}>
              <AstrinContactInformation />
            </Grid>
            <Grid item xs={6} md={12}>
              <NIHSupportTag />
            </Grid>
            <Grid item xs={12}>
              <SocialMediaLinks />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={7}>
          <ContactForm />
        </Grid>
      </Grid>
    </PageSection>
  );
}

/**
 * * Astrin address and contact information
 * @returns {React.ReactElement} AstrinAddress
 */
function AstrinContactInformation() {
  return (
    <div
      id="contact-section-astrin-contact-information"
      data-testid="contact-section-astrin-contact-information"
    >
      <Typography variant="body2" fontWeight="bold" color="common.black">
        info@astrinbio.com
      </Typography>
      <Typography variant="body2" color="common.black">
        Astrin Biosciences
      </Typography>
      <Typography variant="body2" color="common.black">
        1000 Westgate Drive
      </Typography>
      <Typography variant="body2" color="common.black">
        St. Paul, MN 55114
      </Typography>
    </div>
  );
}

/**
 * * NIH support tag
 * @returns {React.ReactElement} NIHSupportTag
 */
function NIHSupportTag() {
  const greaterThanMd = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const { lazyLoadOffset } = usePageContext();
  return (
    <div
      id="contact-section-nih-support-tag"
      data-testid="contact-section-nih-support-tag"
    >
      <Typography variant="body2" color="common.black">
        Supported by the
      </Typography>
      <LazyLoad offset={lazyLoadOffset}>
        <img
          src={NIHLogo}
          width={greaterThanMd ? "50%" : "80%"}
          alt="NIH Logo"
        />
      </LazyLoad>
    </div>
  );
}

/**
 * * Social media links
 * @returns {React.ReactElement} SocialMediaLinks
 */
function SocialMediaLinks() {
  const iconSpacing = { mr: { md: 2, lg: 4, xl: 6 } };
  return (
    <Box
      id="contact-section-social-media-links"
      data-testid="contact-section-social-media-links"
      display="flex"
      justifyContent={{ xs: "space-evenly", md: "flex-start" }}
      my={{ xs: 1, md: 0 }}
    >
      <IconButton
        id="contact-section-facebook-icon"
        data-testid="contact-section-icon"
        aria-label="Facebook"
        sx={iconSpacing}
        onClick={() =>
          window.open(
            "https://www.facebook.com/profile.php?id=100094756566505",
            "_blank"
          )
        }
      >
        <FacebookRounded fontSize="large" />
      </IconButton>
      <IconButton
        id="contact-section-twitter-icon"
        data-testid="contact-section-icon"
        aria-label="Twitter"
        sx={iconSpacing}
        onClick={() => window.open("https://www.x.com/astrinbio", "_blank")}
      >
        <Twitter fontSize="large" />
      </IconButton>
      <IconButton
        id="contact-section-linkedin-icon"
        data-testid="contact-section-icon"
        aria-label="LinkedIn"
        onClick={() =>
          window.open(
            "https://www.linkedin.com/company/astrin-biosciences",
            "_blank"
          )
        }
        sx={iconSpacing}
      >
        <LinkedIn fontSize="large" />
      </IconButton>
      <IconButton
        id="contact-section-instagram-icon"
        data-testid="contact-section-icon"
        aria-label="Instagram"
        onClick={() =>
          window.open("https://www.instagram.com/astrinbio", "_blank")
        }
      >
        <Instagram fontSize="large" />
      </IconButton>
    </Box>
  );
}

/**
 * * Contact form
 * @returns {React.ReactElement} ContactForm
 * BUG: Astrin-Biosciences/web-astrinbio#3
 * TODO: Add form validation
 * TODO: Add direct form submission
 */
function ContactForm() {
  const greaterThanSm = useMediaQuery((theme) => theme.breakpoints.up("sm"));

  const sxProps = {
    "& .MuiFormLabel-root": {
      color: "secondary.main",
    },
    backgroundColor: "white",
  };

  const inputProps = {
    sx: {
      "& input": {
        color: "secondary.main",
      },
    },
  };

  return (
    <>
      <Box py={1}>
        <Typography variant="body2" fontWeight="bold" color="common.black">
          Interested in discovering more about Astrin Biosciences?
        </Typography>
        <Typography variant="body2" fontWeight="bold" color="common.black">
          Please contact us.
        </Typography>
      </Box>
      <form
        action="mailto:info@astrinbio.com"
        method="post"
        encType="text/plain"
        id="contact-section-form"
        data-testid="contact-section-form"
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              id="contact-section-form-firstName"
              data-testid="contact-section-form-firstName"
              name="firstName"
              label="First Name"
              color="secondary"
              variant="filled"
              autoComplete="given-name"
              sx={sxProps}
              InputProps={inputProps}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              id="contact-section-form-lastName"
              data-testid="contact-section-form-lastName"
              name="lastName"
              label="Last Name"
              color="secondary"
              variant="filled"
              autoComplete="family-name"
              sx={sxProps}
              InputProps={inputProps}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="contact-section-form-email"
              data-testid="contact-section-form-email"
              name="email"
              label="Email Address"
              color="secondary"
              variant="filled"
              autoComplete="email"
              sx={sxProps}
              InputProps={inputProps}
              required
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="contact-section-form-message"
              data-testid="contact-section-form-message"
              name="message"
              label="Message"
              color="secondary"
              variant="filled"
              autoComplete="off"
              sx={sxProps}
              InputProps={inputProps}
              multiline
              required
              fullWidth
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={9}
            order={{ xs: 1, sm: 0 }}
            display="flex"
            alignItems="center"
            justifyContent={{ xs: "center", sm: "left" }}
          >
            <Typography variant="body2" color="common.black" fontSize="0.8rem">
              Copyright 2023 Astrin Biosciences All Rights Reserved
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sm={3}
            display="flex"
            justifyContent={{ xs: "center", sm: "flex-end" }}
          >
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              fullWidth={!greaterThanSm}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
}

// * Export for testing purposes only
// Unused right now, but kept as example
if (process.env.NODE_ENV === "test") {
  module.exports.ContactForm = ContactForm;
}
